'use-client';
import * as React from 'react';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import classNames from 'clsx';
import { HamburgerMenuContextProvider } from '../../HamburgerMenuContext';
import type { IHamburgerMenuRootProps } from '../HamburgerMenuRoot.props';
import classes from './style/HamburgerMenuRoot.scss';
import { useAnimationState } from '../../../Menu/viewer/components/MenuItem/useAnimationState';
import {
  INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
  HAMBURGER_MENU_ANIMATION_CONFIG,
  ANIMATION_PACKAGE_CONFIG,
} from '../../../Menu/viewer/constants';
import type { HamburgerMenuAnimationNameType } from '../../../Menu/Menu.types';
import { HamburgerMenuAnimationName } from '../../../Menu/constants';
import { getAnimationPackage } from '../../../Menu/viewer/utils/animationUtils';

const HamburgerMenuRoot: React.FC<IHamburgerMenuRootProps> = props => {
  const {
    id,
    customClassNames = [],
    children,
    compPreviewState,
    containerProps,
    containerRootClassName,
    hasResponsiveLayout,
    shouldFocus,
    isMenuOpen,
    updateComponentPropsInViewer,
  } = props;

  const animationNameRef: React.MutableRefObject<HamburgerMenuAnimationNameType> =
    React.useRef(HamburgerMenuAnimationName.None);

  const { shouldOmitWrapperLayers } = containerProps ?? {};

  const childrenToRender =
    typeof children === 'function' ? children : () => children;

  const setIsMenuOpen = React.useCallback(
    (isOpen: boolean) => {
      const getHamburgerMenuAnimationName = () => {
        const animationPackage = getAnimationPackage(id);
        const menuItemAnimationName =
          ANIMATION_PACKAGE_CONFIG[animationPackage].hamburgerMenuAnimationName;

        return menuItemAnimationName;
      };

      if (isMenuOpen !== isOpen) {
        animationNameRef.current = getHamburgerMenuAnimationName();
        updateComponentPropsInViewer({ isMenuOpen: isOpen });
      }
    },
    [isMenuOpen, updateComponentPropsInViewer, id],
  );

  const { animationState, initEnterAnimation, initExitAnimation } =
    useAnimationState(
      HAMBURGER_MENU_ANIMATION_CONFIG,
      INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
    );

  return (
    <div
      id={id}
      className={classNames(
        // Semantic classes are applied in HamburgerOpenButton
        formatClassNames(null, ...customClassNames),
        containerRootClassName,
        shouldOmitWrapperLayers && classes.root,
      )}
    >
      <HamburgerMenuContextProvider
        compPreviewState={compPreviewState}
        shouldFocus={shouldFocus}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        animationName={animationNameRef.current}
        animationState={animationState}
        initEnterAnimation={initEnterAnimation}
        initExitAnimation={initExitAnimation}
      >
        {hasResponsiveLayout ? (
          <ResponsiveContainer
            {...containerProps!}
            extraRootClass={classes.root}
          >
            {childrenToRender}
          </ResponsiveContainer>
        ) : (
          childrenToRender()
        )}
      </HamburgerMenuContextProvider>
    </div>
  );
};

export default HamburgerMenuRoot;
